/* eslint-disable import/no-extraneous-dependencies */
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawPolygonMode,
  TerraDrawRenderMode,
  type GeoJSONStoreFeatures,
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { DRAWER_MANAGER_OPTIONS } from '../constants';

export type GeofenceDrawMode = 'circle' | 'polygon' | null;

const GEOFENCE_MAP_ELEMENT_ID = 'geofence-google-map';
const IDLE_DRAW_MODE = 'static';

const TERRA_DRAW_SHAPE_STYLES = {
  fillColor: '#ffc000' as const,
  fillOpacity: DRAWER_MANAGER_OPTIONS.fillOpacity ?? 0.2,
  outlineColor: '#FF0000' as const,
  outlineWidth: DRAWER_MANAGER_OPTIONS.strokeWeight ?? 1,
  outlineOpacity: DRAWER_MANAGER_OPTIONS.strokeOpacity ?? 0.8,
};

function ensureMapElementId(map: google.maps.Map): void {
  const div = map.getDiv();
  if (div && !div.id) {
    div.id = GEOFENCE_MAP_ELEMENT_ID;
  }
}

function extractCircleFromPolygonFeature(
  feature: GeoJSONStoreFeatures,
): { center: google.maps.LatLngLiteral; radius: number } | null {
  if (feature.geometry.type !== 'Polygon') {
    return null;
  }

  const ring = feature.geometry.coordinates[0];
  if (!ring || ring.length < 4) {
    return null;
  }

  const half = Math.floor((ring.length - 1) / 2);
  const center = {
    lat: (ring[0][1] + ring[half][1]) / 2,
    lng: (ring[0][0] + ring[half][0]) / 2,
  };

  let radius = 0;
  if (google.maps.geometry?.spherical) {
    radius = Math.round(
      google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(center.lat, center.lng),
        new google.maps.LatLng(ring[0][1], ring[0][0]),
      ),
    );
  }

  return { center, radius };
}

function createGoogleCircle(
  center: google.maps.LatLngLiteral,
  radius: number,
): google.maps.Circle {
  return new google.maps.Circle({
    center,
    radius,
    fillColor: DRAWER_MANAGER_OPTIONS.fillColor,
    fillOpacity: DRAWER_MANAGER_OPTIONS.fillOpacity,
    strokeColor: DRAWER_MANAGER_OPTIONS.strokeColor,
    strokeOpacity: DRAWER_MANAGER_OPTIONS.strokeOpacity,
    strokeWeight: DRAWER_MANAGER_OPTIONS.strokeWeight,
    clickable: false,
    map: null,
  });
}

function createGooglePolygon(feature: GeoJSONStoreFeatures): google.maps.Polygon | null {
  if (feature.geometry.type !== 'Polygon') {
    return null;
  }

  const ring = feature.geometry.coordinates[0];
  if (!ring?.length) {
    return null;
  }

  const path = ring.map(([lng, lat]) => ({ lat, lng }));

  return new google.maps.Polygon({
    paths: path,
    fillColor: DRAWER_MANAGER_OPTIONS.fillColor,
    fillOpacity: DRAWER_MANAGER_OPTIONS.fillOpacity,
    strokeColor: DRAWER_MANAGER_OPTIONS.strokeColor,
    strokeOpacity: DRAWER_MANAGER_OPTIONS.strokeOpacity,
    strokeWeight: DRAWER_MANAGER_OPTIONS.strokeWeight,
    clickable: false,
    map: null,
  });
}

type UseGeofenceTerraDrawOptions = {
  map: google.maps.Map | null;
  onCircleComplete?: (circle: google.maps.Circle) => void;
  onPolygonComplete?: (polygon: google.maps.Polygon) => void;
};

type UseGeofenceTerraDrawResult = {
  activeDrawMode: GeofenceDrawMode;
  startCircleDraw: () => void;
  startPolygonDraw: () => void;
  cancelDraw: () => void;
  isReady: boolean;
};

export function useGeofenceTerraDraw({
  map,
  onCircleComplete,
  onPolygonComplete,
}: UseGeofenceTerraDrawOptions): UseGeofenceTerraDrawResult {
  const drawRef = useRef<TerraDraw | null>(null);
  const readyRef = useRef(false);
  const pendingModeRef = useRef<GeofenceDrawMode>(null);
  const onCircleCompleteRef = useRef(onCircleComplete);
  const onPolygonCompleteRef = useRef(onPolygonComplete);
  const [activeDrawMode, setActiveDrawMode] = useState<GeofenceDrawMode>(null);
  const [isReady, setIsReady] = useState(false);

  onCircleCompleteRef.current = onCircleComplete;
  onPolygonCompleteRef.current = onPolygonComplete;

  const applyPendingMode = useCallback((): void => {
    const draw = drawRef.current;
    const pendingMode = pendingModeRef.current;
    if (!draw || !readyRef.current) {
      return;
    }

    if (!pendingMode) {
      draw.setMode(IDLE_DRAW_MODE);
      setActiveDrawMode(null);
      return;
    }

    draw.setMode(pendingMode);
    setActiveDrawMode(pendingMode);
  }, []);

  const startCircleDraw = useCallback((): void => {
    pendingModeRef.current = 'circle';
    applyPendingMode();
  }, [applyPendingMode]);

  const startPolygonDraw = useCallback((): void => {
    pendingModeRef.current = 'polygon';
    applyPendingMode();
  }, [applyPendingMode]);

  const cancelDraw = useCallback((): void => {
    pendingModeRef.current = null;
    const draw = drawRef.current;
    if (draw?.enabled) {
      draw.clear();
      draw.setMode(IDLE_DRAW_MODE);
    }
    setActiveDrawMode(null);
  }, []);

  useEffect(() => {
    if (!map) {
      readyRef.current = false;
      setIsReady(false);
      return undefined;
    }

    ensureMapElementId(map);

    let draw: TerraDraw | null = null;
    let projectionListener: google.maps.MapsEventListener | null = null;

    const initDraw = (): void => {
      if (drawRef.current) {
        return;
      }

      draw = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({
          map,
          lib: google.maps,
        }),
        modes: [
          new TerraDrawCircleMode({
            styles: TERRA_DRAW_SHAPE_STYLES,
          }),
          new TerraDrawPolygonMode({
            styles: TERRA_DRAW_SHAPE_STYLES,
          }),
          new TerraDrawRenderMode({
            modeName: IDLE_DRAW_MODE,
            styles: {},
          }),
        ],
      });

      draw.on('ready', () => {
        readyRef.current = true;
        setIsReady(true);
        draw!.setMode(IDLE_DRAW_MODE);
        applyPendingMode();
      });

      draw.on('finish', (id, context) => {
        const feature = draw?.getSnapshotFeature(id);
        if (!feature || !draw) {
          return;
        }

        if (context.mode === 'circle') {
          const circleData = extractCircleFromPolygonFeature(feature);
          if (circleData && onCircleCompleteRef.current) {
            const circle = createGoogleCircle(circleData.center, circleData.radius);
            onCircleCompleteRef.current(circle);
          }
        } else if (context.mode === 'polygon') {
          const polygon = createGooglePolygon(feature);
          if (polygon && onPolygonCompleteRef.current) {
            onPolygonCompleteRef.current(polygon);
          }
        }

        draw.removeFeatures([id]);
        pendingModeRef.current = null;
        setActiveDrawMode(null);
        draw.setMode(IDLE_DRAW_MODE);
      });

      drawRef.current = draw;
      draw.start();
    };

    if (map.getProjection()) {
      initDraw();
    } else {
      projectionListener = google.maps.event.addListenerOnce(map, 'projection_changed', initDraw);
    }

    return () => {
      if (projectionListener) {
        google.maps.event.removeListener(projectionListener);
      }
      if (drawRef.current) {
        drawRef.current.stop();
        drawRef.current = null;
      }
      readyRef.current = false;
      setIsReady(false);
      pendingModeRef.current = null;
      setActiveDrawMode(null);
    };
  }, [map, applyPendingMode]);

  return {
    activeDrawMode,
    startCircleDraw,
    startPolygonDraw,
    cancelDraw,
    isReady,
  };
}
